import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useUser } from '@/contexts/UserContext'
import type { Novedad } from '@/types/novedad'

interface NovedadesResponse {
  novedades: Novedad[]
}

export interface UseNovedadesResult {
  novedades: Novedad[]
  loading: boolean
  error: Error | null
  latestId: string | null
  /** Cuántas novedades hay más nuevas que la última marcada como vista. */
  sinLeer: number
  hasUnseen: boolean
  marcarVisto: () => Promise<void>
}

/** Clave con la que una novedad se guarda en `tours_vistos`. */
const claveVisto = (id: string) => `novedades:${id}`

/**
 * Hook de lectura del feed de Novedades.
 *
 * - Fetchea GET /me/novedades (la API ya entrega el feed ordenado: orden asc,
 *   fecha desc — no se reordena en el cliente) y filtra a las novedades
 *   destinadas a fc-web-app.
 * - `latestId` es la primera entrada del feed filtrado (la más reciente).
 * - `sinLeer` sale del `tours_vistos` que ya viaja en `user` (login/refresh),
 *   sin round-trip extra. Ver abajo por qué es una posición y no un flag.
 * - `marcarVisto` reutiliza el mecanismo genérico de tours: POST
 *   /me/tours/{key}/visto con key `novedades:{latestId}`, luego refreshUser().
 *   Mirror de useTour.ts: fire-and-forget seguro, nunca lanza hacia el caller.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useNovedades(): UseNovedadesResult {
  const { user, refreshUser } = useUser()
  const [novedades, setNovedades] = useState<Novedad[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const fetchNovedades = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<NovedadesResponse>('/me/novedades')
      const filtered = (res.novedades ?? []).filter((n) =>
        n.plataformas.includes('fc-web-app'),
      )
      setNovedades(filtered)
    } catch (e) {
      const err = e instanceof ApiError ? e : new Error('Error al cargar las novedades')
      setError(err)
      setNovedades([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchNovedades()
  }, [fetchNovedades])

  const latestId = novedades[0]?.id ?? null

  /**
   * Cuántas novedades hay sin leer.
   *
   * `marcarVisto` sólo guarda la clave de la MÁS RECIENTE del momento, así que
   * la primera entrada marcada del feed es la marca de agua de la última
   * visita: todo lo que está por encima de ella llegó después. Su índice ES el
   * conteo de novedades nuevas.
   *
   * Se cuenta por posición y no comparando fechas a propósito: hay varias
   * novedades el mismo día, y un `fecha > ultimaVisita` daría por leídas a las
   * hermanas de la que el usuario marcó. El orden del feed lo fija la API y es
   * total, así que la posición no tiene ese empate.
   *
   * Sin ninguna marca (usuario nuevo) el índice es -1 y todo el feed cuenta.
   */
  const sinLeer = useMemo(() => {
    const vistos = user?.tours_vistos ?? {}
    const marcada = novedades.findIndex((n) => vistos[claveVisto(n.id)])
    return marcada === -1 ? novedades.length : marcada
  }, [novedades, user?.tours_vistos])

  const marcarVisto = useCallback(async () => {
    if (!latestId) return
    try {
      await api.post(`/me/tours/${claveVisto(latestId)}/visto`)
      await refreshUser()
    } catch {
      // Fallo silencioso: el badge podrá re-marcar en la próxima apertura.
    }
  }, [latestId, refreshUser])

  return { novedades, loading, error, latestId, sinLeer, hasUnseen: sinLeer > 0, marcarVisto }
}

/**
 * Las MISMAS novedades, compartidas entre la campana del header, el badge del
 * sidebar y la pantalla.
 *
 * No es una optimización de requests: es la regla de "un dato, un número". El
 * badge del sidebar, el chip "N novedades nuevas" de la barra de filtros y los
 * badges `NUEVO` de cada tarjeta son la misma cifra mostrada tres veces. Con
 * tres instancias del hook, marcar como leídas en una dejaba a las otras dos
 * contradiciéndola en pantalla hasta el próximo reload.
 *
 * El provider vive en `AppLayout`, que ya montaba la campana.
 */
const NovedadesContext = createContext<UseNovedadesResult | null>(null)

export function NovedadesProvider({
  value,
  children,
}: {
  value: UseNovedadesResult
  children: React.ReactNode
}) {
  return <NovedadesContext.Provider value={value}>{children}</NovedadesContext.Provider>
}

/**
 * Novedades compartidas. Lanza fuera del provider: la pantalla y la campana
 * viven dentro de `AppLayout`, y un fallback silencioso a un segundo fetch es
 * justamente el bug que el provider existe para evitar.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useNovedadesContext(): UseNovedadesResult {
  const ctx = useContext(NovedadesContext)
  if (!ctx) {
    throw new Error('useNovedadesContext debe usarse dentro de <NovedadesProvider>')
  }
  return ctx
}
